import React from 'react';
import { NavLink, Link, Link as RouterLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../components/AuthContext';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Breadcrumbs,
  Typography,
  Avatar,
  Chip,
  useMediaQuery,
  alpha,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';

// Section 1: Content
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import RssFeedIcon from '@mui/icons-material/RssFeed';
import ForumIcon from '@mui/icons-material/Forum';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';

// Section 2: Users & Engagement
import GroupIcon from '@mui/icons-material/Group';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import RateReviewIcon from '@mui/icons-material/RateReview';

// Section 3: Business
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';

// Section 4: System
import ShieldIcon from '@mui/icons-material/Shield';
import BuildIcon from '@mui/icons-material/Build';
import SyncIcon from '@mui/icons-material/Sync';

const drawerWidth = 272;

const Main = styled('main')(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(2),
  minHeight: 'calc(100vh - 64px)',
  overflowX: 'hidden',
}));

// Section header component
function SectionHeader({ icon, label, color }) {
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1,
      px: 2.5, pt: 2, pb: 0.5,
    }}>
      <Typography sx={{ fontSize: 15, lineHeight: 1 }}>{icon}</Typography>
      <Typography variant="overline" sx={{
        fontSize: 11, fontWeight: 700, letterSpacing: 1.2,
        color: color || 'text.disabled', lineHeight: 1,
      }}>
        {label}
      </Typography>
    </Box>
  );
}

// Styled nav item with section-aware active color
function NavItem({ to, icon, label, activeColor, onClick }) {
  const ac = activeColor || '#4f46e5';
  return (
    <ListItem disablePadding sx={{ px: 1.25, py: 0.25 }}>
      <ListItemButton
        component={NavLink}
        to={to}
        onClick={onClick}
        sx={{
          borderRadius: 2.5,
          py: 0.9,
          px: 1.5,
          transition: 'all 0.2s ease',
          '& .MuiListItemIcon-root': {
            minWidth: 36,
            color: 'text.secondary',
            transition: 'color 0.2s ease',
          },
          '& .MuiListItemText-primary': {
            fontSize: 13.5,
            fontWeight: 500,
            color: 'text.primary',
          },
          '&:hover': {
            bgcolor: alpha(ac, 0.06),
            '& .MuiListItemIcon-root': { color: ac },
          },
          '&.active': {
            bgcolor: alpha(ac, 0.1),
            border: '1px solid',
            borderColor: alpha(ac, 0.2),
            '& .MuiListItemIcon-root': { color: ac },
            '& .MuiListItemText-primary': { fontWeight: 700, color: ac },
          },
        }}
      >
        <ListItemIcon>{icon}</ListItemIcon>
        <ListItemText primary={label} />
      </ListItemButton>
    </ListItem>
  );
}

export default function AdminDashboardLayout({ children, title, breadcrumbs = [], sx }) {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();

  // Close mobile drawer on route change
  React.useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  React.useEffect(() => {
    try {
      document.body.classList.add('has-sidebar');
      document.body.style.setProperty('--sidebar-width', `${drawerWidth}px`);
    } catch {}
    return () => {
      try {
        document.body.classList.remove('has-sidebar');
        document.body.style.removeProperty('--sidebar-width');
      } catch {}
    };
  }, []);

  const handleNavClick = React.useCallback(() => {
    if (!isMdUp) setMobileOpen(false);
  }, [isMdUp]);

  const roles = React.useMemo(() => {
    const r = user?.role ? String(user.role).toLowerCase() : undefined;
    const rs = Array.isArray(user?.roles) ? user.roles.map(x => String(x).toLowerCase()) : (r ? [r] : []);
    return rs;
  }, [user]);

  const isAdmin = roles.includes('admin');
  const isTeacher = roles.includes('teacher');

  const effectiveBreadcrumbs = React.useMemo(() => {
    if (Array.isArray(breadcrumbs) && breadcrumbs.length > 0) return breadcrumbs;
    const base = [{ label: t('nav.home', 'Home'), to: '/' }];
    if (title) base.push({ label: title });
    return base;
  }, [breadcrumbs, title, t]);

  const userInitial = React.useMemo(() => {
    const name = user?.displayName || user?.name || user?.email || '';
    return name.charAt(0).toUpperCase() || '?';
  }, [user]);

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Brand card */}
      <Box
        component={Link}
        to="/admin/create"
        onClick={handleNavClick}
        sx={{
          m: 1.5, p: 2,
          borderRadius: 3,
          textDecoration: 'none',
          color: 'inherit',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #a855f7 100%)',
          boxShadow: '0 4px 20px rgba(79,70,229,0.3)',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 6px 28px rgba(79,70,229,0.4)',
            transform: 'translateY(-1px)',
          },
        }}
        aria-label={t('admin.nav.goToAdminHome', 'Go to Admin Home')}
      >
        <Avatar sx={{
          width: 44, height: 44,
          bgcolor: 'rgba(255,255,255,0.2)',
          border: '2px solid rgba(255,255,255,0.3)',
          fontSize: 22,
        }}>
          🎓
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{
            fontWeight: 800, fontSize: 16, lineHeight: 1.2,
            color: '#fff', letterSpacing: 0.2,
          }}>
            {t('header.admin.brand', 'Admin Dashboard')}
          </Typography>
          <Typography sx={{
            fontSize: 11, color: 'rgba(255,255,255,0.7)',
            fontWeight: 500, mt: 0.25,
          }}>
            EduMee.vn
          </Typography>
        </Box>
        {/* Mobile close button */}
        {!isMdUp && (
          <IconButton
            size="small"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMobileOpen(false); }}
            sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff' } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Navigation */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pb: 2 }}>
        {/* ── Section 1: Content ── */}
        <SectionHeader icon="📚" label={t('header.admin.sectionContent', 'Content')} color="#4f46e5" />
        <List disablePadding>
          {isAdmin && (
            <NavItem
              to="/admin/subjects"
              icon={<MenuBookIcon fontSize="small" />}
              label={t('header.admin.subjects', 'Subjects')}
              activeColor="#4f46e5"
              onClick={handleNavClick}
            />
          )}
          {(isAdmin || isTeacher) && (
            <NavItem
              to="/admin/create"
              icon={<AutoStoriesIcon fontSize="small" />}
              label={t('header.admin.create', 'Lessons & Practice')}
              activeColor="#7c3aed"
              onClick={handleNavClick}
            />
          )}
          {isAdmin && (
            <NavItem
              to="/admin/exam-papers"
              icon={<HistoryEduIcon fontSize="small" />}
              label={t('header.admin.examPapers', 'Exam Papers')}
              activeColor="#a855f7"
              onClick={handleNavClick}
            />
          )}
          {isAdmin && (
            <NavItem
              to="/admin/blogs"
              icon={<RssFeedIcon fontSize="small" />}
              label={t('header.admin.blogs', 'Blog Management')}
              activeColor="#6366f1"
              onClick={handleNavClick}
            />
          )}
          {isAdmin && (
            <NavItem
              to="/admin/posts"
              icon={<ForumIcon fontSize="small" />}
              label={t('header.admin.reports', 'Posts & Reports')}
              activeColor="#8b5cf6"
              onClick={handleNavClick}
            />
          )}
        </List>

        <Divider sx={{ mx: 2.5, my: 0.5, borderColor: alpha('#000', 0.06) }} />

        {/* ── Section 2: Users & Engagement ── */}
        {isAdmin && (
          <>
            <SectionHeader icon="👥" label={t('header.admin.sectionUsers', 'Users & Engagement')} color="#0891b2" />
            <List disablePadding>
              <NavItem
                to="/admin/users"
                icon={<GroupIcon fontSize="small" />}
                label={t('header.admin.users', 'Users & Roles')}
                activeColor="#0891b2"
                onClick={handleNavClick}
              />
              <NavItem
                to="/admin/points"
                icon={<EmojiEventsIcon fontSize="small" />}
                label={t('header.admin.points', 'Points & Rewards')}
                activeColor="#0d9488"
                onClick={handleNavClick}
              />
              <NavItem
                to="/admin/feedback"
                icon={<RateReviewIcon fontSize="small" />}
                label={t('header.admin.feedback', 'Student Feedback')}
                activeColor="#06b6d4"
                onClick={handleNavClick}
              />
            </List>

            <Divider sx={{ mx: 2.5, my: 0.5, borderColor: alpha('#000', 0.06) }} />
          </>
        )}

        {/* ── Section 3: Business ── */}
        {isAdmin && (
          <>
            <SectionHeader icon="💰" label={t('header.admin.sectionBusiness', 'Business')} color="#d97706" />
            <List disablePadding>
              <NavItem
                to="/admin/subscriptions"
                icon={<CardMembershipIcon fontSize="small" />}
                label={t('header.admin.subscriptions', 'Subscriptions')}
                activeColor="#d97706"
                onClick={handleNavClick}
              />
              <NavItem
                to="/admin/payments"
                icon={<ReceiptLongIcon fontSize="small" />}
                label={t('header.admin.payments', 'Payments')}
                activeColor="#ea580c"
                onClick={handleNavClick}
              />
              <NavItem
                to="/admin/payment-methods"
                icon={<AccountBalanceWalletIcon fontSize="small" />}
                label={t('header.admin.paymentMethods', 'Payment Methods')}
                activeColor="#f59e0b"
                onClick={handleNavClick}
              />
            </List>

            <Divider sx={{ mx: 2.5, my: 0.5, borderColor: alpha('#000', 0.06) }} />
          </>
        )}

        {/* ── Section 4: System ── */}
        {isAdmin && (
          <>
            <SectionHeader icon="🔧" label={t('header.admin.sectionSystem', 'System')} color="#64748b" />
            <List disablePadding>
              <NavItem
                to="/admin/sensitive"
                icon={<ShieldIcon fontSize="small" />}
                label={t('header.admin.sensitive', 'Content Safety')}
                activeColor="#64748b"
                onClick={handleNavClick}
              />
              <NavItem
                to="/admin/maintenance"
                icon={<BuildIcon fontSize="small" />}
                label={t('header.admin.maintenance', 'Maintenance')}
                activeColor="#94a3b8"
                onClick={handleNavClick}
              />
              <NavItem
                to="/admin/sync"
                icon={<SyncIcon fontSize="small" />}
                label={t('header.admin.sync', 'Data Sync')}
                activeColor="#94a3b8"
                onClick={handleNavClick}
              />
            </List>
          </>
        )}
      </Box>

      {/* Footer with user info */}
      <Divider />
      <Box sx={{
        p: 1.5,
        display: 'flex', alignItems: 'center', gap: 1.5,
        background: alpha('#f8fafc', 0.8),
      }}>
        <Avatar sx={{
          width: 36, height: 36,
          bgcolor: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
          background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
          fontSize: 14, fontWeight: 700,
          border: '2px solid',
          borderColor: alpha('#4f46e5', 0.2),
        }}>
          {user?.avatar ? (
            <Box component="img" src={user.avatar} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
          ) : userInitial}
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{
            fontSize: 13, fontWeight: 600,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {user?.displayName || user?.name || user?.email || 'Admin'}
          </Typography>
          <Chip
            size="small"
            label={isAdmin ? t('common.admin', 'Admin') : t('common.teacher', 'Teacher')}
            sx={{
              height: 18, fontSize: 10, fontWeight: 700,
              bgcolor: isAdmin
                ? alpha('#4f46e5', 0.1)
                : alpha('#0891b2', 0.1),
              color: isAdmin ? '#4f46e5' : '#0891b2',
              borderRadius: 1,
            }}
          />
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {!isMdUp && (
        <IconButton
          aria-label={t('admin.nav.openSidebar', 'Open sidebar')}
          onClick={() => setMobileOpen(true)}
          sx={{
            position: 'fixed',
            top: 'calc(env(safe-area-inset-top) + 8px)',
            left: 8,
            zIndex: (t) => t.zIndex.appBar + 1,
            width: 38,
            height: 38,
            borderRadius: '10px',
            bgcolor: 'transparent',
            color: theme.palette.mode === 'dark' ? '#a5b4fc' : '#7c3aed',
            transition: 'background 0.15s ease, color 0.15s ease, transform 0.15s ease',
            '&:hover': {
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(99,102,241,0.15)' : '#ede9fe',
              color: theme.palette.mode === 'dark' ? '#c7d2fe' : '#6d28d9',
              transform: 'translateY(-1px)',
            },
          }}
        >
          <MenuIcon />
        </IconButton>
      )}

      <Drawer
        variant={isMdUp ? 'permanent' : 'temporary'}
        open={isMdUp ? true : mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ disableScrollLock: true }}
        sx={{
          width: { xs: 'min(92vw, 320px)', md: drawerWidth },
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: { xs: 'min(92vw, 320px)', md: drawerWidth },
            boxSizing: 'border-box',
            top: 0,
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            borderRight: '1px solid',
            borderColor: alpha('#000', 0.08),
            backgroundColor: '#fafbfc',
          },
        }}
      >
        {drawer}
      </Drawer>

      <Main>
        <Box sx={{ mb: 2 }}>
          {effectiveBreadcrumbs && effectiveBreadcrumbs.length > 0 && !(effectiveBreadcrumbs.length === 1 && !effectiveBreadcrumbs[0].to) && (location.pathname !== '/') && (
            <Box sx={{
              px: { xs: 1.5, sm: 2.5 }, py: { xs: 1, sm: 1.25 },
              borderRadius: 3,
              background: (t) => t.palette.mode === 'dark'
                ? 'linear-gradient(135deg, rgba(14,165,233,0.12) 0%, rgba(59,130,246,0.08) 100%)'
                : 'linear-gradient(135deg, #eff6ff 0%, #f0f9ff 50%, #ecfeff 100%)',
              border: (t) => `1px solid ${t.palette.mode === 'dark' ? 'rgba(14,165,233,0.15)' : 'rgba(14,165,233,0.12)'}`,
              ...sx,
            }}>
              <Breadcrumbs
                aria-label="breadcrumb"
                separator={<ChevronRightRoundedIcon sx={{ fontSize: 18, color: 'rgba(14,165,233,0.5)' }} />}
                sx={{
                  '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap' },
                  '& .MuiBreadcrumbs-li': { whiteSpace: 'nowrap' },
                }}
              >
                {effectiveBreadcrumbs.map((bc, idx) => {
                  const isLast = !bc.to;
                  return isLast ? (
                    <Box key={idx} sx={{
                      display: 'inline-flex', alignItems: 'center', gap: 0.5,
                      px: 1.5, py: 0.4, borderRadius: 2,
                      background: 'linear-gradient(135deg, #0284c7, #0ea5e9)',
                      color: '#fff', fontWeight: 700, fontSize: { xs: '0.78rem', sm: '0.82rem' },
                      boxShadow: '0 2px 8px rgba(14,165,233,0.25)',
                    }}>
                      {bc.label}
                    </Box>
                  ) : (
                    <Link
                      key={idx}
                      component={RouterLink}
                      to={bc.to}
                      underline="none"
                      sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 0.5,
                        px: 1.25, py: 0.3, borderRadius: 1.5,
                        fontSize: { xs: '0.78rem', sm: '0.82rem' }, fontWeight: 600,
                        color: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : '#6b7280',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(14,165,233,0.15)' : 'rgba(14,165,233,0.08)',
                          color: '#0284c7',
                        },
                      }}
                    >
                      {idx === 0 && <HomeRoundedIcon sx={{ fontSize: 16 }} />}
                      {bc.label}
                    </Link>
                  );
                })}
              </Breadcrumbs>
            </Box>
          )}
        </Box>
        {children}
      </Main>
    </Box>
  );
}
